/**
 * Encryption Configuration
 * 
 * This file centralizes all AES encryption configuration.
 * Environment variables are loaded from .env file.
 * 
 * For Vite: Use import.meta.env.VITE_*
 * For Create React App: Use process.env.REACT_APP_*
 */

import CryptoJS from "crypto-js";

// Detect if we're using Vite or Create React App
const isVite = typeof import.meta !== 'undefined' && import.meta.env;

// Get environment variables (works with both Vite and CRA)
const getEnvVar = (key) => {
  if (isVite) {
    return import.meta.env[key] || import.meta.env[`VITE_${key.replace('REACT_APP_', '')}`];
  }
  return process.env[key];
};

// AES Encryption Configuration
const AES_KEY = getEnvVar('VITE_AES_KEY') || getEnvVar('REACT_APP_AES_KEY') || "7D3B8E69F1A84B7EA8A3F6792B44C926";
const AES_IV = getEnvVar('VITE_AES_IV') || getEnvVar('REACT_APP_AES_IV') || "A1B2C3D4E5F60718";

/**
 * Encrypt text using AES encryption
 * @param {string} text - Text to encrypt
 * @returns {string} - Encrypted text
 */
const encryptText = (text) => {
  try {
    if (!text) return "";
    const key = CryptoJS.enc.Utf8.parse(AES_KEY);
    const iv = CryptoJS.enc.Utf8.parse(AES_IV);
    const encrypted = CryptoJS.AES.encrypt(text, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  } catch (error) {
    console.error("Encryption failed", error);
    return "";
  }
};

/**
 * Decrypt text using AES decryption
 * @param {string} encryptedText - Encrypted text to decrypt
 * @returns {string|null} - Decrypted text or null if decryption fails
 */
const decryptText = (encryptedText) => {
  try {
    if (!encryptedText) return null;
    const key = CryptoJS.enc.Utf8.parse(AES_KEY);
    const iv = CryptoJS.enc.Utf8.parse(AES_IV);
    const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error("Decryption failed", error);
    return null;
  }
};

// Export configuration and helper functions
export default {
  AES_KEY,
  AES_IV,
  encryptText,
  decryptText,
};

// Named exports for convenience
export {
  AES_KEY,
  AES_IV,
  encryptText,
  decryptText,
};

